
  import { defineComponent, ref, computed, watch } from "vue"

  import { useRouter } from "vue-router"

  import { ArrowNarrowDownIcon, DotsHorizontalIcon, ArrowNarrowRightIcon } from "@heroicons/vue/solid"

  import { StatesMap, PropertyTypesList, ExecutionTypesList, FirmTypesMap } from "@/core/constants"
  import { convertToCurrency, convertToShortCurrency, converToNumber } from "@/core/helpers/convertToCurrency"

  export default defineComponent({
    name: "BorrowerSignupQuizStep",

    components: {
      ArrowNarrowDownIcon,
      DotsHorizontalIcon,
      ArrowNarrowRightIcon
    },

    emits: ['nextStep'],

    setup (props, { emit }) {
      const router = useRouter()

      const steps = ref<any>({
        loanType: {
          title: 'Is this a business loan or an investment property loan?',
          options: ['Business loan', 'Investment Property'],
          collectedData: '',
          isActive: true,
          isCompleted: false,
          nextStep: 'propertyType',
          type: 'selector'
        },
        propertyType: {
          title: 'What type of property are you inquiring about?',
          subtitle: 'If the property is a mix of multifamily and other uses such as office or retail, please select "Multifamily" as your property type.',
          options: ['Multifamily 5+ Units', 'Office', 'Retail', 'Industrial', 'Hospitality', '1-4 Unit Residential', 'Self Storage', 'Mobile Home Park'],
          collectedData: '',
          model: 'propertyType',
          isActive: false,
          isCompleted: false,
          nextStep: 'loanAmount',
          type: 'selector'
        },
        loanAmount: {
          title: 'What is the requested loan amount?',
          subtitle: 'If you aren\'t entirely sure, please make your best educated guess.',
          collectedData: {
            loanAmount: ''
          },
          type: 'loanAmount',
          isActive: false,
          isCompleted: false,
          nextStep: 'contacts'
        },
        contacts: {
          title: 'Please fill out your contact information',
          collectedData: {
            firstName: '',
            lastName: '',
            email: '',
            phone: ''
          },
          type: 'contacts',
          isActive: false,
          isCompleted: false,
          nextStep: 'submit'
        },
        submit: {
          title: 'You\'ve got 75 lender matches!',
          subtitle: 'Let\'s dig deeper and build the perfect loan package for the best possible quotes from the right lenders.',
          type: 'submit'
        }
      })

      const visibleSteps = computed(() => {
        return Object.fromEntries(
          Object.entries(steps.value).filter((item: any) => {
            return item[1].isActive
          })
        )
      })

      const updateValue = (model, value) => {
        steps.value[model].collectedData = value
      }

      const processNextStep = (currentStep, nextStep) => {
        steps.value[nextStep].isActive = true
        steps.value[currentStep].isCompleted = true

        if (currentStep == 'contacts') {
          const payload = {}

          for (const key in steps.value) {
            payload[key] = steps.value[key].collectedData
          }

          window.localStorage.setItem(
            'borrowerDeal',
            JSON.stringify(payload)
          )
        }

        emit('nextStep', steps.value)
      }

      const validSteps = computed(() => {
        const obj = {}

        for (const key in steps.value) {
          const { type, collectedData } = steps.value[key]

          if (type == 'selector') {
            obj[key] = !!collectedData
          } else if (type == 'loanAmount') {
            obj[key] = !!collectedData.loanAmount
          } else if (type == 'contacts') {
            obj[key] = !Object.values(collectedData).filter(item => !item).length
          }else {
            obj[key] = false
          }
        }

        return obj
      })

      watch(steps, (newValue, oldValue) => {
        if (steps.value.loanAmount.collectedData.loanAmount) {
          steps.value.loanAmount.collectedData.loanAmount = convertToCurrency(steps.value.loanAmount.collectedData.loanAmount)
        }
      }, { deep: true })

      return {
        steps,
        visibleSteps,
        updateValue,
        processNextStep,
        convertToShortCurrency,
        validSteps
      }
    }
  })
